import type { Recording, Segment } from './types'
import { mediaToWall, speedFactor, wallToMedia } from './time'
import { analysisTimeline } from './playback'

export const MIN_CONFIDENCE = 0.8

export function isRealtimeRecording(recording: Recording) {
  return Math.abs(speedFactor(recording) - 1) <= 0.15
}

// Keep playback destinations independent of viewport zoom and visual aggregation.
export function speechRegions(
  segments: Segment[],
  start: number,
  end: number,
  audioSegments: Segment[] = [],
): Segment[] {
  if (!audioSegments.length) {
    return extractSpeechRegions(segments, start, end)
  }
  const externalRegions = extractSpeechRegions(audioSegments, start, end)
  const mapped: Segment[] = []
  for (const video of segments) {
    if (!isRealtimeRecording(video.recording)) continue
    for (const region of externalRegions) {
      const from = Math.max(start, video.start, region.start)
      const to = Math.min(end, video.end, region.end)
      if (to > from) mapped.push({ start: from, end: to, recording: video.recording })
    }
  }
  for (const region of extractSpeechRegions(segments, start, end)) {
    const coveredByAudio = audioSegments.some(
      (a) => a.start < region.end && a.end > region.start,
    )
    if (!coveredByAudio) mapped.push(region)
  }
  return mapped.sort((a, b) => a.start - b.start || a.end - b.end)
}

function extractSpeechRegions(
  segments: Segment[],
  start: number,
  end: number,
): Segment[] {
  return segments
    .flatMap((segment) => {
      const r = segment.recording
      if (r.voice?.status !== 'ready') return []
      return r.voice.segments.flatMap((voice) => {
        if (voice.confidence < MIN_CONFIDENCE) return []
        const analysis = analysisTimeline(r)
        const mediaStart = Math.max(analysis.mediaStart, voice.start)
        const mediaEnd = Math.min(analysis.mediaEnd, voice.end)
        if (mediaEnd <= mediaStart) return []
        const from = Math.max(
          start,
          segment.start,
          mediaToWall(analysis, mediaStart / 1000),
        )
        const to = Math.min(end, segment.end, mediaToWall(analysis, mediaEnd / 1000))
        return to > from ? [{ start: from, end: to, recording: r }] : []
      })
    })
    .sort((a, b) => a.start - b.start || a.end - b.end)
}

export function speechTarget(regions: Segment[], clock: number, direction: -1 | 1) {
  if (direction === 1) return regions.find((region) => region.start > clock)
  const current = regions.find((region) => region.start <= clock && clock < region.end)
  if (
    current &&
    wallToMedia(current.recording, clock) -
      wallToMedia(current.recording, current.start) >=
      3
  )
    return current
  for (let i = regions.length - 1; i >= 0; i--)
    if (regions[i].end <= clock) return regions[i]
}

export function speechStatus(recording: Recording, utc: number) {
  const analysis = recording.voice
  if (!analysis) return 'Speech information unavailable'
  if (analysis.status === 'no_audio') return 'No audio track'
  if (!analysis.segments.length) return 'No speech detected'
  const qualifying = analysis.segments.filter((s) => s.confidence >= MIN_CONFIDENCE)
  if (!qualifying.length) return 'No speech above threshold'
  const media = wallToMedia(analysisTimeline(recording), utc) * 1000
  return qualifying.some((s) => media >= s.start && media < s.end)
    ? 'Speech region'
    : 'Outside detected speech regions'
}
